// client/src/components/configuracoes/plataformaNotaFiscal/PlataformaNotaFiscalConfigPage.js
// Nota fiscal da mensalidade SaaS — matriz emitindo em nome da própria
// plataforma (Onmicare/SituAI), cobrando Franquia/Conta. Espelha
// NotaFiscalConfigPage.js (Conta), mas config é única — sem Account pra
// emprestar CNPJ/razão social de, então esses campos entram direto aqui.
import React, { useState, useEffect, useCallback } from 'react';
import { Receipt, Save, Info, ShieldCheck } from 'lucide-react';
import PageHeader from '../../layout/PageHeader';
import { usePlataformaNotaFiscalConfig, useSalvarPlataformaNotaFiscalConfig } from '../../../hooks/usePlataformaNotaFiscal';

const ToggleSwitch = ({ checked, onChange, label }) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
        <div
            onClick={() => onChange(!checked)}
            style={{
                width: 44, height: 24, borderRadius: 12, cursor: 'pointer', position: 'relative', flexShrink: 0,
                background: checked ? 'var(--color-primary)' : '#d1d5db',
                transition: 'background 0.2s',
            }}
        >
            <div style={{
                position: 'absolute', top: 2, width: 20, height: 20, borderRadius: '50%',
                background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                left: checked ? 22 : 2,
                transition: 'left 0.2s',
            }} />
        </div>
        <span style={{ fontWeight: 500, fontSize: '0.875rem' }}>{label}</span>
    </label>
);

const REGIMES = [
    { value: '', label: 'Selecione...' },
    { value: 'simples_nacional', label: 'Simples Nacional' },
    { value: 'lucro_presumido', label: 'Lucro Presumido' },
    { value: 'lucro_real', label: 'Lucro Real' },
    { value: 'mei', label: 'MEI' },
];

const CAMPOS_CREDENCIAL_POR_PROVIDER = {
    mock: [],
    plugnotas: [{ name: 'apiKey', label: 'API Key' }],
    focusnfe: [{ name: 'token', label: 'Token de Acesso' }],
};

const FORM_VAZIO = {
    provider: 'mock',
    ambiente: 'sandbox',
    ativo: false,
    emitirAutomaticamente: false,
    cnpj: '', razaoSocial: '', nomeFantasia: '',
    inscricaoMunicipal: '', inscricaoEstadual: '', regimeTributario: '',
    codigoServicoMunicipal: '', aliquotaIss: '',
    cep: '', logradouro: '', numero: '', complemento: '', bairro: '', localidade: '', uf: '', codigoIbge: '',
};

const PlataformaNotaFiscalConfigPage = () => {
    const { data: config, isLoading, isError, error } = usePlataformaNotaFiscalConfig();
    const salvarConfig = useSalvarPlataformaNotaFiscalConfig();

    const [form, setForm] = useState(FORM_VAZIO);
    const [credenciais, setCredenciais] = useState({});

    useEffect(() => {
        if (config) {
            const de = config.dadosEmitente || {};
            const end = de.endereco || {};
            setForm({
                provider: config.provider || 'mock',
                ambiente: config.ambiente || 'sandbox',
                ativo: config.ativo ?? false,
                emitirAutomaticamente: config.emitirAutomaticamente ?? false,
                cnpj: de.cnpj || '', razaoSocial: de.razaoSocial || '', nomeFantasia: de.nomeFantasia || '',
                inscricaoMunicipal: de.inscricaoMunicipal || '', inscricaoEstadual: de.inscricaoEstadual || '',
                regimeTributario: de.regimeTributario || '', codigoServicoMunicipal: de.codigoServicoMunicipal || '',
                aliquotaIss: de.aliquotaIss ?? '',
                cep: end.cep || '', logradouro: end.logradouro || '', numero: end.numero || '',
                complemento: end.complemento || '', bairro: end.bairro || '', localidade: end.localidade || '',
                uf: end.uf || '', codigoIbge: end.codigoIbge || '',
            });
        }
    }, [config]);

    const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

    const handleSubmit = useCallback((e) => {
        e.preventDefault();
        const payload = {
            provider: form.provider,
            ambiente: form.ambiente,
            ativo: form.ativo,
            emitirAutomaticamente: form.emitirAutomaticamente,
            dadosEmitente: {
                cnpj: form.cnpj || undefined,
                razaoSocial: form.razaoSocial || undefined,
                nomeFantasia: form.nomeFantasia || undefined,
                inscricaoMunicipal: form.inscricaoMunicipal || undefined,
                inscricaoEstadual: form.inscricaoEstadual || undefined,
                regimeTributario: form.regimeTributario || undefined,
                codigoServicoMunicipal: form.codigoServicoMunicipal || undefined,
                aliquotaIss: form.aliquotaIss !== '' ? Number(form.aliquotaIss) : undefined,
                endereco: {
                    cep: form.cep || undefined, logradouro: form.logradouro || undefined, numero: form.numero || undefined,
                    complemento: form.complemento || undefined, bairro: form.bairro || undefined,
                    localidade: form.localidade || undefined, uf: form.uf || undefined, codigoIbge: form.codigoIbge || undefined,
                },
            },
        };
        const camposCredencial = CAMPOS_CREDENCIAL_POR_PROVIDER[form.provider] || [];
        const credenciaisPreenchidas = camposCredencial.filter((c) => credenciais[c.name]);
        if (credenciaisPreenchidas.length > 0) {
            payload.credenciais = {};
            credenciaisPreenchidas.forEach((c) => { payload.credenciais[c.name] = credenciais[c.name]; });
        }
        salvarConfig.mutate(payload);
    }, [form, credenciais, salvarConfig]);

    if (isLoading) return null;

    if (isError) {
        return (
            <div className="page-management-container">
                <PageHeader icon={<Receipt />} title="Nota Fiscal da Mensalidade" subtitle="Configure a emissão fiscal da plataforma" />
                <div className="table-empty-state">
                    <h3>Não foi possível carregar</h3>
                    <p>{error?.response?.data?.error || error?.response?.data?.message || 'Erro ao carregar a configuração de nota fiscal da plataforma.'}</p>
                </div>
            </div>
        );
    }

    const camposCredencial = CAMPOS_CREDENCIAL_POR_PROVIDER[form.provider] || [];

    return (
        <div className="page-management-container">
            <PageHeader icon={<Receipt />} title="Nota Fiscal da Mensalidade" subtitle="Nota emitida em nome da plataforma, cobrando Franquia/Conta" />
            <form onSubmit={handleSubmit}>
                <div className="content-panel">
                    <div className="form-section">
                        <h4 className="form-section-title">Status</h4>
                        <div className="form-group">
                            <ToggleSwitch checked={form.ativo} onChange={(v) => setField('ativo', v)} label={form.ativo ? 'Emissão fiscal ativa' : 'Emissão fiscal desativada'} />
                        </div>
                        <div className="form-group">
                            <ToggleSwitch
                                checked={form.emitirAutomaticamente}
                                onChange={(v) => setField('emitirAutomaticamente', v)}
                                label={form.emitirAutomaticamente ? 'Emite automaticamente quando uma mensalidade é confirmada paga' : 'Emissão automática desligada'}
                            />
                        </div>
                        <p className="form-hint" style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>
                            <Info size={14} /> Dispara sozinha a cada cobrança recorrente confirmada pelo Pagar.me (Franquia ou Conta). Franquia ainda não gera registro financeiro — nota fiscal de Franquia fica pendente até isso ser resolvido.
                        </p>
                    </div>

                    <div className="form-section">
                        <h4 className="form-section-title">Provedor</h4>
                        <div className="form-grid-container-cols-2">
                            <div className="form-group">
                                <label className="form-label">Provedor de emissão</label>
                                <select className="form-input" value={form.provider} onChange={(e) => setField('provider', e.target.value)}>
                                    <option value="mock">Mock (teste, nunca sai da rede)</option>
                                    <option value="plugnotas">PlugNotas</option>
                                    <option value="focusnfe">Focus NFe</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Ambiente</label>
                                <select className="form-input" value={form.ambiente} onChange={(e) => setField('ambiente', e.target.value)}>
                                    <option value="sandbox">Sandbox (teste)</option>
                                    <option value="producao">Produção</option>
                                </select>
                            </div>
                        </div>
                        {camposCredencial.length > 0 && (
                            <div className="form-grid-container-cols-2">
                                {camposCredencial.map((c) => (
                                    <div className="form-group" key={c.name}>
                                        <label className="form-label">{c.label}</label>
                                        <input
                                            type="password"
                                            className="form-input"
                                            placeholder={config?.temCredenciais ? 'Já configurada — deixe em branco pra manter' : 'Digite a credencial'}
                                            value={credenciais[c.name] || ''}
                                            onChange={(e) => setCredenciais((prev) => ({ ...prev, [c.name]: e.target.value }))}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                        {form.provider === 'plugnotas' && (
                            <p className="form-hint" style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>
                                <ShieldCheck size={14} /> Antes de emitir de verdade, cadastre o CNPJ da plataforma com um certificado digital A1 na interface do PlugNotas (fora daqui — certificado é material sensível demais pra passar por qualquer outro lugar).
                            </p>
                        )}
                    </div>

                    <div className="form-section">
                        <h4 className="form-section-title">Dados do Emitente (a própria plataforma)</h4>
                        <div className="form-grid-container-cols-2">
                            <div className="form-group">
                                <label className="form-label required">CNPJ</label>
                                <input className="form-input" value={form.cnpj} onChange={(e) => setField('cnpj', e.target.value)} placeholder="Somente números" />
                            </div>
                            <div className="form-group">
                                <label className="form-label required">Razão Social</label>
                                <input className="form-input" value={form.razaoSocial} onChange={(e) => setField('razaoSocial', e.target.value)} />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Nome Fantasia</label>
                                <input className="form-input" value={form.nomeFantasia} onChange={(e) => setField('nomeFantasia', e.target.value)} />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Regime Tributário</label>
                                <select className="form-input" value={form.regimeTributario} onChange={(e) => setField('regimeTributario', e.target.value)}>
                                    {REGIMES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Inscrição Municipal</label>
                                <input className="form-input" value={form.inscricaoMunicipal} onChange={(e) => setField('inscricaoMunicipal', e.target.value)} />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Inscrição Estadual</label>
                                <input className="form-input" value={form.inscricaoEstadual} onChange={(e) => setField('inscricaoEstadual', e.target.value)} />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Código do Serviço (LC 116)</label>
                                <input className="form-input" value={form.codigoServicoMunicipal} onChange={(e) => setField('codigoServicoMunicipal', e.target.value)} placeholder="Ex: 0107" />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Alíquota ISS (%)</label>
                                <input type="number" min="0" max="100" step="0.01" className="form-input" value={form.aliquotaIss} onChange={(e) => setField('aliquotaIss', e.target.value)} />
                            </div>
                        </div>
                    </div>

                    <div className="form-section">
                        <h4 className="form-section-title">Endereço Fiscal</h4>
                        <div className="form-grid-container-cols-2">
                            <div className="form-group">
                                <label className="form-label">CEP</label>
                                <input className="form-input" value={form.cep} onChange={(e) => setField('cep', e.target.value)} />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Logradouro</label>
                                <input className="form-input" value={form.logradouro} onChange={(e) => setField('logradouro', e.target.value)} />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Número</label>
                                <input className="form-input" value={form.numero} onChange={(e) => setField('numero', e.target.value)} />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Complemento</label>
                                <input className="form-input" value={form.complemento} onChange={(e) => setField('complemento', e.target.value)} />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Bairro</label>
                                <input className="form-input" value={form.bairro} onChange={(e) => setField('bairro', e.target.value)} />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Cidade</label>
                                <input className="form-input" value={form.localidade} onChange={(e) => setField('localidade', e.target.value)} />
                            </div>
                            <div className="form-group">
                                <label className="form-label">UF</label>
                                <input className="form-input" maxLength={2} value={form.uf} onChange={(e) => setField('uf', e.target.value.toUpperCase())} />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Código IBGE do Município</label>
                                <input className="form-input" value={form.codigoIbge} onChange={(e) => setField('codigoIbge', e.target.value)} />
                            </div>
                        </div>
                    </div>

                    <div className="form-actions">
                        <button type="submit" className="action-button action-button-primary" disabled={salvarConfig.isPending}>
                            <Save size={18} className="icon" /> {salvarConfig.isPending ? 'Salvando...' : 'Salvar'}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default PlataformaNotaFiscalConfigPage;
